import { FC, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as CloseIcon } from 'src/assets/svg/close.svg'
import { ReactComponent as SearchIcon } from 'src/assets/svg/searchIconGray.svg'
import CardFilterButton from 'src/components/CardFilterButton'
import { IndividualPost } from 'src/services/__generated__/api'
import styles from './individualPostsModal.module.scss'
import { PostCard } from '../PostCard'

interface IndividualPostsModalProps {
  data: IndividualPost[]
  isOpen: boolean
  onClose: () => void
  onOpenFilter?: () => void
  onViewDetails: (post: IndividualPost) => void
}

export const IndividualPostsModal: FC<IndividualPostsModalProps> = ({
  data,
  isOpen,
  onClose,
  onOpenFilter,
  onViewDetails,
}) => {
  const [searchQuery, setSearchQuery] = useState('')

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return data
    const query = searchQuery.toLowerCase()
    return data.filter(
      (post) =>
        (post.content || '').toLowerCase().includes(query) ||
        (post.platform || '').toLowerCase().includes(query)
    )
  }, [data, searchQuery])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleClose = () => {
    setSearchQuery('')
    onClose()
  }

  if (!isOpen) return null

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={handleClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Individual Posts</h2>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.searchAndFilterRow}>
          <div className={styles.searchRow}>
            <SearchIcon className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              onChange={handleSearchChange}
              placeholder="Search"
              type="text"
              value={searchQuery}
            />
          </div>

          {onOpenFilter && <CardFilterButton onClick={onOpenFilter} />}
        </div>

        <div className={styles.postsList}>
          {filteredPosts.length === 0 ? (
            <div className={styles.emptyState}>No posts found</div>
          ) : (
            filteredPosts.map((post) => (
              <PostCard
                key={post.id}
                onViewDetails={() => onViewDetails(post)}
                post={post}
                variant="vertical"
              />
            ))
          )}
        </div>
      </div>
    </>,
    document.body
  )
}
